import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, ZoomIn, ArrowRight } from "lucide-react";
import { LOGO_ICON } from "@/config";
import { useI18n } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Gallery = () => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios.get(`${API}/gallery`).then(({ data }) => {
      setItems(data.slice(0, 6).map((d) => ({ id: d.id, src: `${process.env.REACT_APP_BACKEND_URL}${d.url}` })));
    }).catch(() => setItems([]));
  }, []);

  const HOME_ITEMS = items;
  const prev = (e) => { e.stopPropagation(); setActive((i) => (i - 1 + HOME_ITEMS.length) % HOME_ITEMS.length); };
  const next = (e) => { e.stopPropagation(); setActive((i) => (i + 1) % HOME_ITEMS.length); };

  if (!items.length) return null;

  return (
    <section id="gallery" className="py-24 bg-white" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand mb-3">{t.gallery.eyebrow}</p>
            <h2 className="font-head text-3xl sm:text-4xl font-semibold text-navy tracking-tight">{t.gallery.title}</h2>
            <p className="mt-4 text-slate-600">{t.gallery.subtitle}</p>
          </div>
          <Link
            to="/galeria"
            data-testid="view-full-gallery-btn"
            className="btn-lift inline-flex items-center gap-2 self-start bg-navy hover:bg-navy-deep text-white font-semibold px-6 py-3.5 rounded-md"
          >
            {t.gallery.viewFull} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          {HOME_ITEMS.map((img, i) => (
            <motion.button
              key={i}
              data-testid={`gallery-thumb-${i}`}
              onClick={() => setActive(i)}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.05 }}
              className="group relative aspect-square overflow-hidden rounded-md bg-slate-100"
            >
              <img src={img.src} alt="Sedlo Services" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <img src={LOGO_ICON} alt="" aria-hidden="true" className="pointer-events-none absolute bottom-2 right-2 w-12 h-auto opacity-25 drop-shadow" />
              <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/30 transition-colors flex items-center justify-center">
                <ZoomIn className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[100] bg-navy-deep/95 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <button data-testid="lightbox-close" aria-label="Zavrieť" onClick={() => setActive(null)} className="absolute top-5 right-5 text-white/80 hover:text-white p-2"><X className="w-8 h-8" /></button>
            <button data-testid="lightbox-prev" aria-label="Predchádzajúca fotka" onClick={prev} className="absolute left-3 sm:left-8 text-white/70 hover:text-white p-2"><ChevronLeft className="w-9 h-9" /></button>
            <button data-testid="lightbox-next" aria-label="Ďalšia fotka" onClick={next} className="absolute right-3 sm:right-8 text-white/70 hover:text-white p-2"><ChevronRight className="w-9 h-9" /></button>
            <motion.div key={active} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="relative max-w-4xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
              <img src={HOME_ITEMS[active].src} alt="Sedlo Services" className="max-h-[85vh] w-auto rounded-md object-contain" />
              <img src={LOGO_ICON} alt="" aria-hidden="true" className="pointer-events-none absolute bottom-3 right-3 w-16 h-auto opacity-25 drop-shadow-lg" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
